"use client";

import { useEffect, useState } from "react";
import { X, Minimize2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatMessageProps } from "./chat-message";
import { ChatInput } from "@/components/ui/chat-input";
import { Conversation, ConversationContent, ConversationScrollButton } from "@/components/ui/shadcn-io/ai/conversation";
import { Message, MessageContent, MessageAvatar } from "@/components/ui/shadcn-io/ai/message";
import { Response } from "@/components/ui/shadcn-io/ai/response";
import { BlinkingDots } from "@/components/ui/shadcn-io/ai/loader";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useAppSelector } from "@/redux/hooks";

export interface ChatWindowProps {
  messages: ChatMessageProps[];
  onSendMessage: (message: string) => void | Promise<void>;
  onClose: () => void;
  onClear?: () => void | Promise<void>;
  onMinimize?: () => void;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  className?: string;
  suggestedPrompts?: Array<{ icon: string; label: string; prompt: string }>;
}

const defaultSuggestedPrompts = [
  {
    icon: "🚀",
    label: "Get Started",
    prompt: "What can you help me with?",
  },
  {
    icon: "📚",
    label: "Learn More",
    prompt: "Show me the documentation",
  },
  {
    icon: "❓",
    label: "Help",
    prompt: "How do I use this platform?",
  },
  {
    icon: "⚙️",
    label: "Settings",
    prompt: "Take me to settings",
  },
];

export function ChatWindow({
  messages,
  onSendMessage,
  onClose,
  onClear,
  onMinimize,
  isLoading = false,
  title = "AIOS Assistant",
  subtitle = "Always here to help",
  className,
  suggestedPrompts = defaultSuggestedPrompts,
}: ChatWindowProps) {
  const [showSuggestions, setShowSuggestions] = useState(true);
  const currentUser = useAppSelector((state) => state.user.user);

  useEffect(() => {
    setShowSuggestions(messages.length === 0);
  }, [messages]);

  const handleSuggestedPromptClick = (prompt: string) => {
    setShowSuggestions(false);
    onSendMessage(prompt);
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full w-full bg-background border rounded-3xl shadow-2xl overflow-hidden",
        "animate-in fade-in duration-200 ease-out",
        className
      )}
    >
      {/* Header with Gradient */}
      <div className="relative flex items-center justify-between px-6 py-4 border-b overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5" />

        <div className="relative flex items-center gap-3 z-10">
          <div className="h-10 w-10 rounded-full bg-transparent flex items-center justify-center">
            <Image
              src="/Icon_logo_light.svg"
              alt="Implement AI"
              width={24}
              height={24}
              className="text-white"
            />
          </div>
          <div>
            <h3 className="font-semibold text-sm flex items-center gap-2">
              {title}
             
            </h3>
          </div>
        </div>

        <div className="relative flex items-center gap-1 z-10">
          {onMinimize && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full hover:bg-primary/10 transition-colors"
              onClick={onMinimize}
            >
              <Minimize2 className="h-4 w-4" />
              <span className="sr-only">Minimize</span>
            </Button>
          )}
          {onClear && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full hover:bg-primary/10 transition-colors disabled:opacity-50"
              onClick={() => onClear()}
              disabled={isLoading}
              title="Clear chat"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="sr-only">Clear chat</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full hover:bg-destructive/10 transition-colors"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>
      </div>

      {/* Messages Area - shadcn Conversation + Message */}
      <Conversation className="flex-1 min-h-0">
        <ConversationContent className="px-6 py-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] text-center py-8 space-y-6">
              {/* Hero Section */}
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/30 flex items-center justify-center mb-4 animate-in zoom-in duration-500">
                  <Image
                    src="/Icon_logo_light.svg"
                    alt="Implement AI"
                    width={40}
                    height={40}
                  />
                </div>
              </div>

              <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
                <h4 className="font-bold text-xl bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
                  How can I assist you?
                </h4>
                <p className="text-sm text-muted-foreground max-w-[280px]">
                  Choose a quick action below or ask me anything
                </p>
              </div>

              {/* Suggested Prompts Grid */}
              {showSuggestions && (
                <div className="grid grid-cols-2 gap-2 w-full max-w-[320px] animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                  {suggestedPrompts.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedPromptClick(suggestion.prompt)}
                      className={cn(
                        "group relative p-4 rounded-2xl border bg-gradient-to-br ",
                        "hover:from-primary/5 hover:to-primary/10 hover:border-primary/30",
                        "transition-all duration-300 hover:scale-105 hover:shadow-lg",
                        "text-left space-y-2"
                      )}
                      style={{
                        animationDelay: `${(index + 1) * 100}ms`,
                      }}
                    >
                      <div className="text-2xl">{suggestion.icon}</div>
                      <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                        {suggestion.label}
                      </p>
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-primary/10 transition-all duration-300" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const isLastMessage = index === messages.length - 1;
                const isLastAssistantAndLoading =
                  isLoading &&
                  isLastMessage &&
                  !msg.isUser &&
                  !msg.message?.trim();
                const from = msg.isUser ? "user" : "assistant";
                const avatarSrc = msg.isUser
                  ? (msg.avatar ?? currentUser?.profile_image_url ?? "")
                  : (msg.avatar ?? "/logos/iai-logo.png");
                const avatarName = msg.isUser
                  ? (msg.name ?? currentUser?.fullname ?? "You")
                  : (msg.name ?? "AI");

                return (
                
                    <Message
                      key={msg.id ?? `msg-${index}`}
                      from={from as "user" | "assistant"}
                    >
                      <MessageContent className="overflow-hidden break-words max-w-full">
                        {msg.message ? (
                          <Response className="break-words overflow-wrap-anywhere [&_a]:text-primary [&_a]:underline [&_a:hover]:opacity-80">
                            {msg.message}
                          </Response>
                        ) : null}
                        {isLastAssistantAndLoading && (
                          <div className="flex items-center text-black mt-1">
                            <BlinkingDots size="md" />
                          </div>
                        )}
                      </MessageContent>
                      <MessageAvatar src={avatarSrc} name={avatarName} />
                    </Message>
                    
                );
              })}
            </>
          )}

          {/* Typing indicator - only when waiting for first assistant reply */}
          {isLoading &&
            (messages.length === 0 || messages[messages.length - 1]?.isUser) && (
              <Message from="assistant">
                <MessageContent className="overflow-hidden max-w-full">
                  <BlinkingDots size="md" className="text-primary" />
                </MessageContent>
                <MessageAvatar src="/logos/iai-logo.png" name="AI" />
              </Message>
            )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      {/* Input Area - input stays enabled when loading; only send is disabled */}
      <div className="relative px-6 py-4 border-t bg-background">
        <ChatInput
          onSend={onSendMessage}
          isLoading={isLoading}
          placeholder="Ask me anything..."
        />
      </div>
    </div>
  );
}
